import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackGameComplete, trackGameRestart } from '../utils/analytics';
import './GameStyles.css';

const SIZE = 4;
const BEST_KEY = 'ideapetal_2048_best';

const emptyBoard = () => Array.from({ length: SIZE }, () => Array(SIZE).fill(0));

const cloneBoard = (board) => board.map((row) => [...row]);

const addRandomTile = (board) => {
  const next = cloneBoard(board);
  const empty = [];

  next.forEach((row, y) => {
    row.forEach((value, x) => {
      if (value === 0) empty.push({ x, y });
    });
  });

  if (empty.length === 0) {
    return next;
  }

  const cell = empty[Math.floor(Math.random() * empty.length)];
  next[cell.y][cell.x] = Math.random() > 0.12 ? 2 : 4;
  return next;
};

const createBoard = () => addRandomTile(addRandomTile(emptyBoard()));

const slideValues = (values) => {
  const compact = values.filter(Boolean);
  const merged = [];
  let score = 0;

  for (let index = 0; index < compact.length; index += 1) {
    if (compact[index] === compact[index + 1]) {
      const value = compact[index] * 2;
      merged.push(value);
      score += value;
      index += 1;
    } else {
      merged.push(compact[index]);
    }
  }

  while (merged.length < SIZE) merged.push(0);
  return { values: merged, score };
};

const boardsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const moveBoard = (board, direction) => {
  const next = emptyBoard();
  let gained = 0;

  for (let i = 0; i < SIZE; i += 1) {
    const values = [];
    for (let j = 0; j < SIZE; j += 1) {
      if (direction === 'left') values.push(board[i][j]);
      if (direction === 'right') values.push(board[i][SIZE - 1 - j]);
      if (direction === 'up') values.push(board[j][i]);
      if (direction === 'down') values.push(board[SIZE - 1 - j][i]);
    }

    const result = slideValues(values);
    gained += result.score;

    for (let j = 0; j < SIZE; j += 1) {
      if (direction === 'left') next[i][j] = result.values[j];
      if (direction === 'right') next[i][SIZE - 1 - j] = result.values[j];
      if (direction === 'up') next[j][i] = result.values[j];
      if (direction === 'down') next[SIZE - 1 - j][i] = result.values[j];
    }
  }

  return { board: next, gained };
};

const hasMoves = (board) => {
  if (board.some((row) => row.includes(0))) return true;

  for (let y = 0; y < SIZE; y += 1) {
    for (let x = 0; x < SIZE; x += 1) {
      if (board[y][x] === board[y][x + 1] || (board[y + 1] && board[y][x] === board[y + 1][x])) {
        return true;
      }
    }
  }

  return false;
};

const Petal2048 = ({ game }) => {
  const [board, setBoard] = useState(createBoard);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY) || 0));
  const [finished, setFinished] = useState(false);
  const touchStart = useRef(null);

  const restart = useCallback(() => {
    setBoard(createBoard());
    setScore(0);
    setFinished(false);
    trackGameRestart(game);
  }, [game]);

  const move = useCallback((direction) => {
    if (finished) return;

    setBoard((currentBoard) => {
      const result = moveBoard(currentBoard, direction);
      if (boardsEqual(currentBoard, result.board)) {
        return currentBoard;
      }

      const nextBoard = addRandomTile(result.board);
      const nextScore = score + result.gained;
      setScore(nextScore);
      if (nextScore > best) {
        setBest(nextScore);
        localStorage.setItem(BEST_KEY, String(nextScore));
      }
      if (!hasMoves(nextBoard)) {
        setFinished(true);
        trackGameComplete(game, { result: 'game_over', score: nextScore });
      }
      return nextBoard;
    });
  }, [best, finished, game, score]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const keys = {
        ArrowUp: 'up',
        w: 'up',
        W: 'up',
        ArrowDown: 'down',
        s: 'down',
        S: 'down',
        ArrowLeft: 'left',
        a: 'left',
        A: 'left',
        ArrowRight: 'right',
        d: 'right',
        D: 'right',
      };

      if (keys[event.key]) {
        event.preventDefault();
        move(keys[event.key]);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [move]);

  const tiles = useMemo(() => board.flat(), [board]);

  const onTouchStart = (event) => {
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (event) => {
    if (!touchStart.current) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchStart.current = null;

    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      move(dx > 0 ? 'right' : 'left');
    } else {
      move(dy > 0 ? 'down' : 'up');
    }
  };

  return (
    <div className="playable-game">
      <div className="game-toolbar">
        <div className="game-stats">
          <span className="stat-pill">Score {score}</span>
          <span className="stat-pill">Best {best}</span>
        </div>
        <div className="game-actions">
          <button className="game-action-button" onClick={restart} type="button">
            Restart
          </button>
        </div>
      </div>

      {finished && <div className="game-status">No moves left. Restart for a fresh board.</div>}

      <div
        className="puzzle-2048-board"
        onTouchEnd={onTouchEnd}
        onTouchStart={onTouchStart}
        aria-label="Petal 2048 board"
      >
        {tiles.map((value, index) => (
          <div className={`tile-2048 value-${value}`} key={`${index}-${value}`}>
            {value || ''}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Petal2048;
